import { useEffect, useState } from 'react';
import { Box, TextField, Typography, MenuItem, Divider, Button } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Add, Bolt, Send, CheckCircle } from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';
import { getClients } from '../../../services/clientService';
import { getQuoteCreatePolicy } from '../../../services/quoteService';
import { fetchRateAt } from '../../../services/scheduleExchangeRates';
import { currencyOptions } from '../../../constants/statusOptions';
import { roleAtLeast } from '../../../constants/roles';
import { usePermissions } from '../../../hooks/usePermissions';
import { fxRateLabel } from '../../../utils/quoteCurrency';
import { QuickAddClientDialog } from './QuickAddClientDialog';
import { ProjectTitlePicker } from '../ProjectTitlePicker';
import type { QuoteDetail, QuoteStatus } from '../../../types/quote';
import type { Client, Project } from '../../../types/project';

/** Upper bound for a sane FX rate; rejects fat-finger entries. */
const MAX_FX_RATE = 100000;

/** Sentinel value for the "Add new client…" MenuItem (not a real ClientKey). */
const ADD_NEW_CLIENT = '__add_new_client__';

/** Quote-type options for the prominent selector. REQ-01. */
const QUOTE_TYPES: { value: 'Internal' | 'External'; label: string; desc: string; Icon: SvgIconComponent }[] = [
  { value: 'Internal', label: 'Internal', desc: 'Skips approval — schedule directly', Icon: Bolt },
  { value: 'External', label: 'External', desc: 'Full approval before sending to client', Icon: Send },
];

interface QuoteHeaderSectionProps {
  quote: QuoteDetail;
  onUpdate: (data: Record<string, unknown>) => void;
  readOnly?: boolean;
}

/**
 * Statuses where FxRateAtQuote is locked (read-only).
 * Mirrors the POST_SENT_STATUSES set in backend quoteService.ts.
 */
const POST_SENT_STATUSES: QuoteStatus[] = [
  'Sent', 'Viewed', 'Accepted', 'Declined', 'Expired', 'Withdrawn', 'Converted',
];

function isFxRateLocked(status: QuoteStatus): boolean {
  return POST_SENT_STATUSES.includes(status);
}

export const QuoteHeaderSection = ({ quote, onUpdate, readOnly }: QuoteHeaderSectionProps) => {
  const theme = useTheme();
  const { role } = usePermissions();
  const [clients, setClients] = useState<Client[]>([]);
  const [addClientOpen, setAddClientOpen] = useState(false);
  // BUG-2026-0135: client quick-add follows the admin-tunable quote-create
  // policy (default technician+). Roles below it don't get the "Add new
  // client…" item — it would only 403 on the backend.
  const [clientCreateMinRole, setClientCreateMinRole] = useState('admin');

  const currency = (quote.Currency ?? 'CAD').toUpperCase();
  const fxLocked = isFxRateLocked(quote.Status);
  const isCad = currency === 'CAD';
  const fxEditable = !readOnly && !fxLocked && !isCad;

  // Controlled draft of the FX-rate input so the user can type freely; validated
  // and persisted on blur / Enter / Recalculate. Kept in sync with the saved
  // rate whenever the quote changes from outside (e.g. after a softRefresh).
  const [fxInput, setFxInput] = useState<string>(
    quote.FxRateAtQuote != null ? quote.FxRateAtQuote.toFixed(4) : '',
  );
  const [fxError, setFxError] = useState<string | null>(null);
  // Set when a non-CAD currency has no saved admin rate — prompt manual entry.
  const [noSavedRate, setNoSavedRate] = useState(false);

  const loadClients = () => getClients().then(setClients).catch(() => {});

  useEffect(() => {
    loadClients();
    getQuoteCreatePolicy()
      .then((p) => setClientCreateMinRole(p.minRole))
      .catch(() => { /* keep the technician default */ });
  }, []);

  const canQuickAddClient = !readOnly && roleAtLeast(role, clientCreateMinRole);

  // Re-sync the input with the persisted rate when the saved value or currency
  // changes (covers prefill-on-currency-change and external refreshes).
  useEffect(() => {
    setFxInput(quote.FxRateAtQuote != null ? quote.FxRateAtQuote.toFixed(4) : '');
    setFxError(null);
  }, [quote.FxRateAtQuote, quote.Currency]);

  const handleBlur = (field: string, value: unknown) => {
    if ((quote as unknown as Record<string, unknown>)[field] !== value) {
      onUpdate({ [field]: value });
    }
  };

  /**
   * Currency change: CAD pins the rate to 1.0; a non-CAD currency prefills the
   * rate from the saved admin Exchange-Rate period for the quote's issue date
   * (today if unset). When no period covers that date we still switch currency
   * but flag the user to enter the rate manually.
   */
  const handleCurrencyChange = async (next: string) => {
    const nextCur = next.toUpperCase();
    if (nextCur === quote.Currency?.toUpperCase()) return;
    setFxError(null);
    setNoSavedRate(false);

    if (nextCur === 'CAD') {
      onUpdate({ Currency: 'CAD', FxRateAtQuote: 1 });
      return;
    }

    const effectiveDate = (quote.IssueDate ?? new Date().toISOString()).split('T')[0];
    try {
      const found = await fetchRateAt(effectiveDate, nextCur);
      if (found) {
        onUpdate({ Currency: nextCur, FxRateAtQuote: found.rateToCAD });
      } else {
        setNoSavedRate(true);
        onUpdate({ Currency: nextCur });
      }
    } catch {
      // On lookup failure, still switch currency; user can enter the rate.
      setNoSavedRate(true);
      onUpdate({ Currency: nextCur });
    }
  };

  /** Validate the typed FX rate and persist it; returns false (and sets an error) when invalid. */
  const commitFxRate = (): boolean => {
    const raw = fxInput.trim();
    if (raw === '') {
      setFxError('Enter a rate');
      return false;
    }
    const value = Number(raw);
    if (!Number.isFinite(value) || value <= 0 || value > MAX_FX_RATE) {
      setFxError(`Rate must be a number between 0 and ${MAX_FX_RATE}`);
      return false;
    }
    setFxError(null);
    setNoSavedRate(false);
    if (value !== quote.FxRateAtQuote) {
      onUpdate({ FxRateAtQuote: value });
    }
    return true;
  };

  const handleClientChange = (value: string) => {
    if (value === ADD_NEW_CLIENT) {
      setAddClientOpen(true);
      return;
    }
    handleBlur('ClientKey', value || null);
  };

  /** After quick-add: refresh the list and select the new client on the quote. */
  const handleClientCreated = async (created: Client) => {
    setAddClientOpen(false);
    await loadClients();
    handleBlur('ClientKey', created.ClientKey);
  };

  /**
   * BUG-2026-0202: Title picked from / created as a project. When the quote has
   * no client yet, inherit the project's client (never overwrite an existing
   * one — same rule the backend applies at conversion).
   */
  const handleProjectTitleCommit = (title: string | null, project?: Project) => {
    const data: Record<string, unknown> = {};
    if ((quote.Title ?? null) !== title) data.Title = title;
    if (project?.ClientKey && !quote.ClientKey) data.ClientKey = project.ClientKey;
    if (Object.keys(data).length > 0) onUpdate(data);
  };

  /** Helper text shown below the FX rate field (RateToCAD semantics: "1 USD = 1.4100 CAD"). */
  const fxHelperText = (() => {
    if (fxError) return fxError;
    if (isCad) return fxRateLabel(currency, 1);
    if (noSavedRate) return 'No saved rate for this currency — enter it manually.';
    const label = fxRateLabel(currency, quote.FxRateAtQuote);
    if (label) return label;
    return 'No rate configured — enter one or add a period in Admin → Exchange Rates.';
  })();

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        Quote Header
      </Typography>

      {/* Quote Type — the most important header field, shown prominently so it's
          obvious at a glance whether this quote skips approval (Internal) or
          goes through the full client workflow (External). REQ-01. */}
      <Box sx={{ mb: 3, maxWidth: 700 }}>
        <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.6, display: 'block', mb: 0.75 }}>
          Quote Type
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          {QUOTE_TYPES.map((t) => {
            const sel = (quote.QuoteType ?? 'External') === t.value;
            return (
              <Box
                key={t.value}
                role="button"
                aria-pressed={sel}
                onClick={() => { if (!readOnly && !sel) handleBlur('QuoteType', t.value); }}
                sx={{
                  cursor: readOnly ? 'default' : 'pointer',
                  // Selected: crisp sage border + tint at full strength. Unselected:
                  // a recessed grey wash (NOT white) + faint border, dimmed to read
                  // clearly as inactive; it brightens to full on hover so it still
                  // signals "clickable".
                  border: `2px solid ${sel ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.12)}`,
                  bgcolor: sel ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.text.primary, 0.04),
                  borderRadius: 2, p: 1.75, display: 'flex', gap: 1.5, alignItems: 'flex-start',
                  transition: 'border-color 0.15s ease, background-color 0.15s ease, opacity 0.15s ease',
                  opacity: sel ? 1 : 0.6,
                  '&:hover': (!readOnly && !sel) ? {
                    opacity: 1,
                    borderColor: alpha(theme.palette.primary.main, 0.5),
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                  } : undefined,
                }}
              >
                <t.Icon sx={{ fontSize: 28, color: sel ? 'primary.main' : 'text.disabled', mt: 0.25, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: sel ? 'primary.main' : 'text.primary' }}>
                      {t.label}
                    </Typography>
                    {sel && <CheckCircle sx={{ fontSize: 18, color: 'primary.main' }} />}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mt: 0.25 }}>
                    {t.desc}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Divider sx={{ mb: 3, maxWidth: 700 }} />

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5, maxWidth: 700 }}>
        <TextField
          select
          label="Client"
          value={quote.ClientKey ?? ''}
          onChange={(e) => handleClientChange(e.target.value)}
          size="small"
          fullWidth
          disabled={readOnly}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {clients.map((c) => (
            <MenuItem key={c.ClientKey} value={c.ClientKey}>
              {c.ClientName}
            </MenuItem>
          ))}
          {canQuickAddClient && <Divider />}
          {canQuickAddClient && (
            <MenuItem value={ADD_NEW_CLIENT} sx={{ color: 'primary.main', fontWeight: 600 }}>
              <Add sx={{ fontSize: 18, mr: 1 }} />
              Add new client…
            </MenuItem>
          )}
        </TextField>

        {/* BUG-2026-0202: the Title IS the project — pick an existing one (or add
            a new one via the New Project dialog) instead of free-typing. */}
        <ProjectTitlePicker
          value={quote.Title}
          disabled={readOnly}
          onCommit={handleProjectTitleCommit}
          helperText="Saved as the quote title; an existing project is reused on conversion."
        />

        <TextField
          label="Issue Date"
          type="date"
          defaultValue={quote.IssueDate?.split('T')[0] ?? ''}
          onBlur={(e) => handleBlur('IssueDate', e.target.value || null)}
          size="small"
          fullWidth
          disabled={readOnly}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Valid Until"
          type="date"
          defaultValue={quote.ValidUntil?.split('T')[0] ?? ''}
          onBlur={(e) => handleBlur('ValidUntil', e.target.value || null)}
          size="small"
          fullWidth
          disabled={readOnly}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          select
          label="Currency"
          value={quote.Currency ?? 'CAD'}
          onChange={(e) => { void handleCurrencyChange(e.target.value); }}
          size="small"
          fullWidth
          disabled={readOnly}
        >
          {currencyOptions.map((c) => (
            <MenuItem key={c} value={c}>{c}</MenuItem>
          ))}
        </TextField>

        {/* FX Rate — editable for non-CAD unlocked quotes; read-only for CAD or
            once the quote is Sent. Totals/PDF derive from CAD / FxRateAtQuote. */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <TextField
            label="FX Rate (to CAD)"
            value={isCad ? '1.0000' : fxInput}
            onChange={(e) => { setFxInput(e.target.value); setFxError(null); }}
            onBlur={() => { if (fxEditable) commitFxRate(); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && fxEditable) {
                e.preventDefault();
                commitFxRate();
              }
            }}
            type={fxEditable ? 'number' : 'text'}
            size="small"
            fullWidth
            disabled={!fxEditable}
            error={!!fxError}
            helperText={fxHelperText}
            InputLabelProps={{ shrink: true }}
            inputProps={fxEditable ? { min: 0, step: 0.0001 } : { readOnly: true }}
            title={
              fxLocked
                ? 'FX rate is locked — the quote has been sent to the client.'
                : isCad
                  ? 'CAD is the base currency — the rate is always 1.0000.'
                  : 'Edit the rate (CAD per 1 unit of the quote currency), or click Recalculate to pull the saved Exchange-Rate period.'
            }
          />
          {fxEditable && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={commitFxRate}
              sx={{ mt: 0.25, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Recalculate
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Prepared By: {quote.PreparedByName ?? quote.PreparedByUserId ?? '—'}
          </Typography>
        </Box>
      </Box>

      <QuickAddClientDialog
        open={addClientOpen}
        onClose={() => setAddClientOpen(false)}
        existing={clients}
        onCreated={handleClientCreated}
      />
    </Box>
  );
};
